/**
 * SourceRatingBadge
 *
 * Compact source attribution with favicon, display name, external link icon,
 * and star rating. Used in meal cards and recipe browser.
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';
import { useTranslation } from 'react-i18next';
import { formatCount, formatRating } from '../../utils/formatNumber';
import { getSourceFaviconUrl, getSourceDisplayName } from '../../utils/recipeSource';

interface SourceRatingBadgeProps {
  sourceUrl: string;
  sourceRating?: number | null;
  sourceRatingCount?: number | null;
  size?: 'sm' | 'md';
  showStars?: boolean;
}

export const SourceRatingBadge: React.FC<SourceRatingBadgeProps> = ({
  sourceUrl,
  sourceRating,
  sourceRatingCount,
  size = 'sm',
  showStars = true,
}) => {
  const { i18n } = useTranslation();
  const faviconUrl = useMemo(() => getSourceFaviconUrl(sourceUrl), [sourceUrl]);
  const displayName = useMemo(() => getSourceDisplayName(sourceUrl), [sourceUrl]);

  const isMd = size === 'md';
  const starSize = isMd ? 13 : 11;
  const faviconSize = isMd ? 16 : 14;
  const nameStyle = isMd ? styles.sourceNameMd : styles.sourceNameSm;
  const ratingStyle = isMd ? styles.ratingValueMd : styles.ratingValueSm;
  const countStyle = isMd ? styles.ratingCountMd : styles.ratingCountSm;

  return (
    <View style={styles.container}><View style={styles.identityRow}>{faviconUrl ? (
          <Image
            source={{ uri: faviconUrl }}
            style={[styles.favicon, { width: faviconSize, height: faviconSize }]}
            resizeMode="contain"
          />
        ) : null}<Text style={nameStyle} numberOfLines={1}>{displayName}</Text><Ionicons name="open-outline" size={isMd ? 12 : 10} color={Paprika.textMuted} style={styles.linkIcon} /></View>{showStars && sourceRating != null && sourceRating > 0 ? (
        <View style={styles.ratingRow}>
          {[1, 2, 3, 4, 5].map((star) => (
            <Ionicons
              key={star}
              name={
                star <= Math.round(sourceRating)
                  ? 'star'
                  : star - 0.5 <= sourceRating
                    ? 'star-half'
                    : 'star-outline'
              }
              size={starSize}
              color="#F5A623"
              style={{ marginRight: 1 }}
            />
          ))}
          <Text style={ratingStyle}>{formatRating(sourceRating, i18n.language)}</Text>
          {sourceRatingCount != null && sourceRatingCount > 0 ? (
            <Text style={countStyle}>({formatCount(sourceRatingCount, i18n.language)})</Text>
          ) : null}
        </View>
      ) : null}</View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.lg,
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: 5,
    alignSelf: 'flex-start',
  },
  identityRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  favicon: {
    borderRadius: 3,
    marginRight: 4,
  },
  linkIcon: {
    marginLeft: 3,
  },
  sourceNameSm: {
    ...PaprikaTypography.caption,
    color: Paprika.textPrimary,
    fontWeight: '600',
    fontSize: 11,
  },
  sourceNameMd: {
    ...PaprikaTypography.caption,
    color: Paprika.textPrimary,
    fontWeight: '600',
    fontSize: 13,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  ratingValueSm: {
    ...PaprikaTypography.caption,
    color: Paprika.textPrimary,
    fontWeight: '600',
    fontSize: 11,
    marginLeft: 3,
  },
  ratingValueMd: {
    ...PaprikaTypography.caption,
    color: Paprika.textPrimary,
    fontWeight: '600',
    fontSize: 13,
    marginLeft: 4,
  },
  ratingCountSm: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    fontSize: 10,
    marginLeft: 2,
  },
  ratingCountMd: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    fontSize: 12,
    marginLeft: 3,
  },
});

export default SourceRatingBadge;
